import React, { useState, useEffect } from "react";
import { Phone, AlertTriangle } from "lucide-react";

interface PhoneNumberModalProps {
    isOpen: boolean;
    onSuccess: (phone: string) => void;
}

const PhoneNumberModal: React.FC<PhoneNumberModalProps> = ({ isOpen, onSuccess }) => {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [isVisible, setIsVisible] = useState(false);

    const currentLanguage = localStorage.getItem('selectedLanguage') || 'az';

    const translations = {
        az: {
            title: "Telefon nömrəsi tələb olunur",
            message: "Kabinetdən istifadə etmək üçün telefon nömrənizi daxil edin.",
            phoneLabel: "Mobil nömrə",
            phonePlaceholder: "XX XXX XX XX",
            phoneError: "Nömrə 9 rəqəmdən ibarət olmalıdır.",
            submit: "Təsdiq et",
            loading: "Göndərilir...",
        },
        ru: {
            title: "Требуется номер телефона",
            message: "Для использования кабинета введите номер телефона.",
            phoneLabel: "Мобильный номер",
            phonePlaceholder: "XX XXX XX XX",
            phoneError: "Номер должен содержать 9 цифр.",
            submit: "Подтвердить",
            loading: "Отправка...",
        },
        en: {
            title: "Phone number required",
            message: "Please enter your phone number to use the cabinet.",
            phoneLabel: "Mobile number",
            phonePlaceholder: "XX XXX XX XX",
            phoneError: "Number must contain 9 digits.",
            submit: "Confirm",
            loading: "Submitting...",
        }
    };

    const t = translations[currentLanguage as keyof typeof translations] || translations.az;

    const formatDigitsWithSpaces = (digits: string) => {
        let res = "";
        for (let i = 0; i < digits.length; i++) {
            if (i === 2 || i === 5 || i === 7) res += " ";
            res += digits[i];
        }
        return res;
    };

    const formattedDigits = formatDigitsWithSpaces(phoneNumber);
    const maskString = "XX XXX XX XX";
    const remainingMask = maskString.slice(formattedDigits.length);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneError("");
        setApiError("");
        let digitsOnly = e.target.value.replace(/\D/g, '');
        
        // Əgər 994 ilə başlayırsa və 9 simvoldan çoxdursa (məsələn copy/paste və ya autofill), 994-ü sil
        if (digitsOnly.startsWith('994') && digitsOnly.length > 9) {
            digitsOnly = digitsOnly.slice(3);
        }

        if (digitsOnly.length <= 9) {
            setPhoneNumber(digitsOnly);
        }
    };

    const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        // No longer need to prepend +994
    };

    const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        let digits = pastedText.replace(/\D/g, '');

        // Əgər kopyalanan mətn +994 və ya 994 ilə başlayırsa, onu silirik
        if (digits.startsWith('994')) {
            digits = digits.slice(3);
        }

        if (digits.length > 0) {
            const newDigits = (phoneNumber + digits).slice(0, 9);
            setPhoneNumber(newDigits);
        }
    };

    const handleSubmit = async () => {
        setPhoneError("");
        setApiError("");

        const digitsOnly = phoneNumber.replace(/\D/g, '');

        // only 9 digits required now since +994 is fixed
        if (digitsOnly.length < 9) {
            setPhoneError(t.phoneError);
            return;
        }

        const cleanPhoneNumber = `994${phoneNumber}`;
        onSuccess(cleanPhoneNumber);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes phoneModalFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes phoneModalSlideUp {
                    from { opacity: 0; transform: translateY(20px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}} />

            <div
                className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
                    isVisible ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ animation: 'phoneModalFadeIn 0.2s ease-out' }}
            >
                {/* Overlay — bağlanmaz */}
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                {/* Modal */}
                <div
                    className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
                    style={{ animation: 'phoneModalSlideUp 0.3s ease-out' }}
                >
                    <div className="p-6 sm:p-8">
                        {/* Icon */}
                        <div className="mx-auto w-16 h-16 mb-4 flex items-center justify-center">
                            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-8 h-8 text-amber-600" />
                            </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                            {t.title}
                        </h3>

                        {/* Message */}
                        <p className="text-gray-600 mb-6 text-center text-sm">
                            {t.message}
                        </p>

                        {/* API Error */}
                        {apiError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                                <p className="text-red-600 text-sm text-center">{apiError}</p>
                            </div>
                        )}

                        {/* Phone Input */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t.phoneLabel}
                            </label>
                            <div className="relative flex items-center w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-pink-300 focus-within:border-transparent transition-all bg-white font-mono text-lg tracking-widest">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <span className="text-gray-900 mr-3 font-medium text-lg tracking-normal">+994</span>
                                
                                <div className="relative flex-1 h-6">
                                    {/* Mask overlay */}
                                    <div className="absolute inset-0 flex items-center pointer-events-none whitespace-pre">
                                        <span className="opacity-0">{formattedDigits}</span>
                                        <span className="text-gray-900">{remainingMask}</span>
                                    </div>
                                    
                                    <input
                                        type="tel"
                                        value={formattedDigits}
                                        onChange={handlePhoneChange}
                                        onFocus={handlePhoneFocus}
                                        onPaste={handlePhonePaste}
                                        onKeyDown={handleKeyDown}
                                        className="absolute inset-0 w-full bg-transparent text-gray-900 focus:outline-none placeholder-transparent caret-pink-600"
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            {phoneError && (
                                <p className="text-red-500 text-sm mt-2">{phoneError}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className={`w-full text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center transition-all ${
                                loading
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:opacity-90 hover:shadow-lg'
                            }`}
                            style={{ backgroundColor: "#a80067" }}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    <span>{t.loading}</span>
                                </>
                            ) : (
                                <span>{t.submit}</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PhoneNumberModal;
